import React, { memo, Suspense, useContext, useEffect, useState } from "react";
import { PlayerStoreContext } from "@common/player/player-context";
import { YoutubeProvider } from "@common/player/providers/youtube/youtube-provider";
import { usePlayerStore } from "@common/player/hooks/use-player-store";
import { HtmlVideoProvider } from "@common/player/providers/html-video-provider";
import { HtmlAudioProvider } from "@common/player/providers/html-audio-provider";
import { NativeAudioProvider } from "@common/player/providers/native-audio-provider";
import {
  canUseNativeAudioProvider,
  canUseNativeAudioPlaybackForMedia,
} from "@common/player/utils/can-use-native-audio-provider";
import { useSettings } from "@ui/settings/use-settings";

const HlsProvider = React.lazy(
  () => import("@common/player/providers/hls-provider"),
);
const DashProvider = React.lazy(
  () => import("@common/player/providers/dash-provider"),
);

interface Props {
  className?: string;
}
export const PlayerOutlet = memo(({ className }: Props) => {
  const { getState } = useContext(PlayerStoreContext);
  const { version } = useSettings();
  const [nativeAudioProviderEnabled, setNativeAudioProviderEnabled] =
    useState(() => canUseNativeAudioProvider());

  useEffect(() => {
    getState().init();
    return getState().destroy;
  }, [getState]);

  useEffect(() => {
    setNativeAudioProviderEnabled(canUseNativeAudioProvider());
  }, [version]);

  useEffect(() => {
    if (nativeAudioProviderEnabled) {
      return;
    }

    const recheckNativeAudioProvider = () => {
      if (canUseNativeAudioProvider()) {
        setNativeAudioProviderEnabled(true);
      }
    };
    const timeoutIds: Array<ReturnType<typeof setTimeout>> = [0, 250, 1000, 3000]
      .map((delay) => setTimeout(recheckNativeAudioProvider, delay));

    if (typeof window !== "undefined") {
      window.addEventListener("focus", recheckNativeAudioProvider);
    }

    if (typeof document !== "undefined") {
      document.addEventListener(
        "visibilitychange",
        recheckNativeAudioProvider,
      );
    }

    return () => {
      timeoutIds.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });

      if (typeof window !== "undefined") {
        window.removeEventListener("focus", recheckNativeAudioProvider);
      }

      if (typeof document !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          recheckNativeAudioProvider,
        );
      }
    };
  }, [nativeAudioProviderEnabled, version]);

  return (
    <div className={className}>
      <Provider />
      {nativeAudioProviderEnabled && <NativeAudioProvider />}
    </div>
  );
});

function Provider() {
  const cuedMedia = usePlayerStore((s) => s.cuedMedia);
  const provider = usePlayerStore((s) => s.providerName);
  const playerType = cuedMedia?.playerType;
  const shouldUseNativePlayback = canUseNativeAudioPlaybackForMedia(cuedMedia);
  const providerKey = `${provider ?? "none"}:${playerType ?? "unknown"}`;

  if (provider && shouldUseNativePlayback && provider !== "youtube") {
    return null;
  }

  switch (provider) {
    case "youtube":
      return <YoutubeProvider key={providerKey} />;
    case "htmlVideo":
      if (playerType === "video") {
        return <HtmlVideoProvider key={providerKey} />;
      }
      return <HtmlAudioProvider key={providerKey} />;
    case "htmlAudio":
      return <HtmlAudioProvider key={providerKey} />;
    case "hls":
      return (
        <Suspense key={providerKey}>
          <HlsProvider key={providerKey} />
        </Suspense>
      );
    case "hlsAudio":
      return (
        <Suspense key={providerKey}>
          <HlsProvider key={providerKey} />
        </Suspense>
      );
    case "dash":
      return (
        <Suspense key={providerKey}>
          <DashProvider key={providerKey} />
        </Suspense>
      );
    case "dashAudio":
      return (
        <Suspense key={providerKey}>
          <DashProvider key={providerKey} />
        </Suspense>
      );
    default:
      return null;
  }
}
